import { get } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useQuery, type UseQueryOptions, type UseQueryResult } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

const workplaceSummarySchema = z.object({
  summary: z.string(),
  qualificationName: z.string(),
  workplaceId: z.string(),
});

export type WorkplaceSummary = z.infer<typeof workplaceSummarySchema>;

const getWorkplaceSummariesResponseSchema = z.object({
  data: z.array(
    z.object({
      id: z.string(),
      type: z.literal("workplace-review-summary"),
      attributes: workplaceSummarySchema,
    })
  ),
});

export type GetWorkplaceSummariesResponse = z.infer<typeof getWorkplaceSummariesResponseSchema>;

function getWorkplaceSummariesUrl(workplaceId: string): string {
  return `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workplaces/${workplaceId}/summaries`;
}

/**
 * We always need the latest summary, hence, sort by createdAt (descending) and limit to 1
 */
function getWorkplaceSummariesQueryParams() {
  return {
    limit: 1,
    sort: "-createdAt",
    format: "markdown", // Used to return markdown summaries for only clients that support it
  };
}

export function useGetWorkplaceSummaries(
  workplaceId: string,
  options: UseQueryOptions<GetWorkplaceSummariesResponse, AxiosError> = {}
): UseQueryResult<GetWorkplaceSummariesResponse> {
  return useQuery({
    queryKey: [getWorkplaceSummariesUrl(workplaceId)],
    queryFn: async () => {
      const response = await get({
        url: getWorkplaceSummariesUrl(workplaceId),
        queryParams: getWorkplaceSummariesQueryParams(),
        responseSchema: getWorkplaceSummariesResponseSchema,
      });
      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_WORKPLACE_SUMMARY_FAILURE,
    },
    ...options,
  });
}
